import Button from "@/components/ui/Button"

interface FeatureProps {
   icon: string
   title: string
   text: string
}

interface MetricProps {
   label: string
   subtitle?: string
   value: string | number
   type?: "currency" | "number" | "text"
}

interface BarProps {
   label: string
   width: number
   color: string
   value?: number
   type?: "currency" | "number" | "text"
}

interface PromoCardProps {
   title: string
   description: string
   buttonText: string
   onClick?: () => void
   type?: "button" | "submit"
}

export function fmtCurrency(n: number) {
   if (!isFinite(n)) return "-"

   const abs = Math.abs(n)
   const sign = n < 0 ? "-" : ""

   let value = abs
   let suffix = ""

   if (abs >= 1000000) {
      value = abs / 1000000
      suffix = "M"
   } else if (abs >= 1000) {
      value = abs / 1000
      suffix = "K"
   }

   const str = value.toLocaleString(undefined, {
      maximumFractionDigits: 1,
   })

   return sign + "$" + str + suffix
}

export default function Feature({ icon, title, text }: FeatureProps) {
   return (
      <div className="flex gap-3">
         <div className="mb-2 text-xl">{icon}</div>
         <div>
            <p className="font-semibold text-[#0D3D73]">{title}</p>
            <p className="text-sm text-gray-400">{text}</p>
         </div>
      </div>
   )
}

export function Metric({ label, subtitle, value, type = "text" }: MetricProps) {
   let displayValue = value
   if (type === "currency" && typeof value === "number") {
      displayValue = fmtCurrency(value)
   }
   if (type === "number" && typeof value === "number") {
      displayValue = value.toLocaleString()
   }
   return (
      <div className="flex flex-col items-center justify-center rounded-lg border p-4 text-center">
         <p className="text-xs text-muted-foreground">{label}</p>
         <p className="text-xs text-muted-foreground">{subtitle}</p>
         <p className="text-lg font-black">{displayValue}</p>
      </div>
   )
}

export function Bar({ label, width, color, value, type = "text" }: BarProps) {
   let formattedValue: string | number | undefined = value
   if (value !== undefined) {
      if (type === "currency") {
         formattedValue = fmtCurrency(value)
      }

      if (type === "number") {
         formattedValue = value.toLocaleString()
      }
   }
   return (
      <div>
         <p className="mb-1 text-xs text-muted-foreground">
            {label} {formattedValue !== undefined && formattedValue}
         </p>
         <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
            <div className={`${color} h-3`} style={{ width: `${width}%` }} />
         </div>
      </div>
   )
}

export function PromoCard({ title, description, buttonText, onClick, type = "button" }: PromoCardProps) {
   return (
      <div className="space-y-2 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-100 p-4 shadow-sm">
         <p className="text-xs font-semibold text-blue-900">{title}</p>
         <p className="text-md font-light text-slate-700">{description}</p>
         <Button type={type} onClick={onClick} className="w-full bg-[#196EB9] text-white hover:bg-[#155a92]">
            {buttonText}
         </Button>
      </div>
   )
}
